// src/lib/api.ts

// VITE_API_BASE  — BookTox resource server (books, reviews, library, etc.)
// VITE_AUTH_BASE — auth-service (login, register, password reset, etc.)
//                  Falls back to VITE_API_BASE so single-server setups still work
//                  (e.g. when a reverse proxy forwards /auth/* to auth-service).
const API_BASE  = (import.meta.env.VITE_API_BASE  ?? "").replace(/\/+$/, "");
const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

function androidBridge() {
    return typeof window !== "undefined" ? (window as any).Android : undefined;
}

function getBridgeTokens(): { access_token?: string; refresh_token?: string } {
    try {
        const bridge = androidBridge();
        if (typeof bridge?.getAuthTokenJson === "function") {
            return JSON.parse(bridge.getAuthTokenJson() || "{}");
        }
    } catch {}
    return {};
}

function access(): string {
    const bridge = androidBridge();
    if (bridge) {
        const t = getBridgeTokens().access_token;
        if (t) return t;
    }
    return localStorage.getItem("bt.access") || "";
}

function refreshToken(): string {
    const bridge = androidBridge();
    if (bridge) {
        const t = getBridgeTokens().refresh_token;
        if (t) return t;
    }
    return localStorage.getItem("bt.refresh") || "";
}

function setTokens(at: string, rt?: string) {
    if (at) localStorage.setItem("bt.access", at);
    if (rt) localStorage.setItem("bt.refresh", rt);
    try {
        const bridge = androidBridge();
        if (typeof bridge?.saveAuthTokenJson === "function") {
            const prev = getBridgeTokens();
            bridge.saveAuthTokenJson(JSON.stringify({
                ...prev,
                ...(at ? { access_token: at } : {}),
                ...(rt ? { refresh_token: rt } : {}),
            }));
        }
    } catch {}
}

async function refreshOnce() {
    const rt = refreshToken();
    if (!rt) return false;
    const r = await fetch(`${AUTH_BASE}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
    });
    if (!r.ok) return false;
    const data = await r.json().catch(() => ({}));
    if (data?.access_token) setTokens(data.access_token, data.refresh_token);
    return !!data?.access_token;
}

type Opt = {
    method?: string;
    headers?: Record<string, string>;
    body?: any;
    json?: any;          // 자동 JSON 직렬화
    raw?: boolean;       // 필요 시 raw response
    signal?: AbortSignal;
    formData?: FormData; // 업로드 등
};

export async function api(path: string, opt: Opt = {}) {
    const isAuthPath = path.startsWith("/auth/") || path === "/auth";
    const base = isAuthPath ? AUTH_BASE : API_BASE;
    // AUTH_BASE already includes /api, so strip /auth prefix (e.g. /auth/me → /me)
    const resolvedPath = isAuthPath ? path.slice("/auth".length) || "/" : path;
    const url = `${base}${resolvedPath.startsWith("/") ? "" : "/"}${resolvedPath}`;
    const h: Record<string, string> = { Accept: "application/json", ...(opt.headers || {}) };
    if (!opt.formData && opt.json !== undefined) h["Content-Type"] = "application/json";
    const init: RequestInit = {
        method: opt.method || (opt.json || opt.formData ? "POST" : "GET"),
        headers: { ...h, ...(access() ? { Authorization: `Bearer ${access()}` } : {}) },
        body: opt.formData ? opt.formData : opt.json !== undefined ? JSON.stringify(opt.json) : opt.body,
        signal: opt.signal,
    };

    let r = await fetch(url, init);
    if (r.status === 401) {
        const ok = await refreshOnce();
        if (ok) {
            init.headers = { ...h, Authorization: `Bearer ${access()}` };
            r = await fetch(url, init);
        }
    }
    if (opt.raw) return r;
    const ctype = r.headers.get("content-type") || "";
    if (!r.ok) {
        // 가능한 한 많이 정보를 붙여서 throw
        const bodyText = await r.text().catch(() => "");
        const err: any = new Error(`HTTP ${r.status}: ${bodyText.slice(0, 200)}`);
        err.status = r.status;
        err.body = bodyText;
        try {
            const j = JSON.parse(bodyText);
            err.json = j;
            err.code = j.error || j.code;  // 백엔드가 {"error":"DATE_TAKEN"} 같이 줄 때
        } catch {}
        throw err;
    }

    // 2xx인데 content-type이 json이 아니면 그냥 text 반환이 필요한 경우가 있을 수 있지만,
    if (!ctype.includes("application/json")) {
        const t = await r.text().catch(() => "");
        const err: any = new Error(`HTTP ${r.status}: Unexpected content-type: ${ctype}. Body: ${t.slice(0, 200)}`);
        err.status = r.status;
        err.body = t;
        throw err;
    }
    return r.json();
}

export async function login(identifier: string, password: string) {
    const r = await fetch(`${AUTH_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ identifier, password, app_id: "booktalk" }),
    });
    const ctype = r.headers.get("content-type") || "";
    if (!r.ok || !ctype.includes("application/json")) {
        const t = await r.text(); throw new Error(`HTTP ${r.status}: ${t.slice(0, 160)}`);
    }
    const data = await r.json();
    setTokens(data.access_token, data.refresh_token);
    localStorage.setItem("bt.user", JSON.stringify(data.user || {}));
    return data;
}

/**
 * 현재 access token. 크로스 오리진 임베드(예: 어드민 통계 iframe)에 토큰을
 * 넘겨야 할 때만 쓴다. 일반 API 호출은 `api()`가 알아서 헤더를 붙인다.
 */
export function getAccessToken(): string {
    return access();
}

export function logout() {
    localStorage.removeItem("bt.access");
    localStorage.removeItem("bt.refresh");
    localStorage.removeItem("bt.user");
    try {
        const bridge = androidBridge();
        if (typeof bridge?.clearAuthToken === "function") bridge.clearAuthToken();
    } catch {}
}

/** 로그인 여부 확인 (토큰 존재 여부만 체크, 만료는 api 호출 시 처리) */
export function isLoggedIn(): boolean {
    const bridge = androidBridge();
    if (bridge) {
        const t = getBridgeTokens();
        if (t.access_token || t.refresh_token) return true;
    }
    return !!(localStorage.getItem("bt.access") || localStorage.getItem("bt.refresh"));
}

export async function me() {
    return api("/auth/me");
}

export async function meWithRoles() {
    return api("/me");
}

// ---- Book store (구매처) API ----
// is_primary: 앱 선택 목록의 "메인" 여부. 서버는 메인/더보기를 한 목록으로 내려주고
// 분리는 클라이언트가 한다. 옵셔널인 이유는 웹이 API보다 먼저 배포됐을 때
// 필드가 없어서 목록이 통째로 비는 사고를 막기 위함 — 없으면 메인으로 취급한다.
// android_package: 칩을 눌렀을 때 실행할 안드로이드 패키지명. null이면 연동할 앱이 없다.
// android_package_eink: 같은 구매처의 e-ink 전용 빌드. 둘 다 있으면 칩을 눌렀을 때 선택 시트가 뜬다.
// 서재 목록(GET /library)의 store에는 실려오지 않으므로 optional이다.
export type BookStore = {
    id: number; code: string; name: string; logo_url?: string | null; is_primary?: boolean;
    android_package?: string | null; android_package_eink?: string | null;
};

/** 활성 구매처 목록 (선택 UI용). 비로그인도 호출 가능. */
export async function bookStoreList(): Promise<{ stores: BookStore[] }> {
    return api("/book-stores");
}

// ---- 약관 동의 (consent) ----
// 로그인 응답의 consent_required 와 같은 판단을 별도로 물어본다.
// 이미 토큰을 들고 앱을 다시 연 경우(로그인을 안 거침)를 잡기 위해 필요하다.

export const CONSENT_APP_ID = "booktalk";

export type ConsentStatus = { app_id: string; required: boolean; missing: string[] };

export async function consentStatus(appId: string = CONSENT_APP_ID): Promise<ConsentStatus> {
    return api(`/auth/consents/status?app_id=${encodeURIComponent(appId)}`);
}

/** 동의 기록. version 은 서버가 apps 행에서 결정하므로 보내지 않는다. */
export async function consentSubmit(
    appId: string,
    consents: Array<{ code: string; agreed: boolean }>,
): Promise<ConsentStatus & { ok: boolean }> {
    return api("/auth/consents", { json: { app_id: appId, consents } });
}

// ---- Library API ----
export type LibraryStatus = 'READING' | 'WISHLIST' | 'FINISHED';

export async function libraryExists(bookId: number | string): Promise<{
    in_library: boolean; status: string | null; store_id: number | null; store: BookStore | null;
}> {
    return api(`/library/exists?book_id=${Number(bookId)}`);
}

/** storeId를 넘기지 않으면 기존 구매처를 유지한다 (null을 넘겨야 선택 해제). */
export async function libraryAdd(
    status: Exclude<LibraryStatus, 'ALL'>,
    book: any,
    storeId?: number | null,
) {
    return api("/library", {
        json: { status, book, ...(storeId !== undefined ? { store_id: storeId } : {}) },
    });
}

/** 서재 목록 응답에 딸려오는 구매처 필터 목록 — 마스터 전체가 아니라 "내 서재에서 실제로 쓰인" 것만. */
export type LibraryFacets = { stores: BookStore[]; has_unassigned: boolean };

/** storeId: 숫자=해당 구매처, 'none'=구매처 미지정, 'ALL'/생략=전체 */
export async function libraryList(params: {
    status?: LibraryStatus | 'ALL';
    store_id?: number | 'none' | 'ALL';
    page?: number;
    size?: number;
    full?: 0 | 1;
} = {}) {
    const s = new URLSearchParams();
    if (params.status)  s.set("status", String(params.status));
    if (params.page)    s.set("page", String(params.page));
    if (params.size)    s.set("size", String(params.size));
    if (params.full)    s.set("full", String(params.full));
    if (params.store_id !== undefined && params.store_id !== 'ALL') {
        s.set("store_id", String(params.store_id));
    }
    return api(`/library?${s.toString()}`);
}
/** 상태만 바꾸려면 status를 그대로 넘기고, 구매처만 바꾸려면 { store_id }를 넘긴다. */
export async function libraryUpdate(
    id: number,
    patch: LibraryStatus | { status?: LibraryStatus; store_id?: number | null },
) {
    const json = typeof patch === "string" ? { status: patch } : patch;
    return api(`/library/${id}`, { method: "PATCH", json });
}
export async function libraryDelete(id: number) {
    return api(`/library/${id}`, { method: 'DELETE' });
}

// ---- Review API ----
export async function fetchReviewKeywords(): Promise<{ id: number; keyword: string }[]> {
    const data = await api('/reviews/keywords');
    return data.keywords ?? [];
}

export const RATING_LABELS: Record<number, string> = {
    1: '내 취향은 아니었어요',
    2: '아쉬워요',
    3: '재밌어요!',
    4: '추천하고 싶어요!',
    5: '인생책이에요!',
};

export async function reviewsMy(params:{page?:number; size?:number}={}) {
    const s = new URLSearchParams();
    if (params.page) s.set('page', String(params.page));
    if (params.size) s.set('size', String(params.size));
    return api(`/reviews/my?${s.toString()}`);
}
export async function reviewGet(id:number){ return api(`/reviews/${id}`); }
export async function reviewCreate(payload:{book_id:number; content:string; rating:number; keywords?:number[]}) {
    return api(`/reviews`, { json: payload });
}
export async function reviewUpdate(id:number, payload:{content:string; rating:number; keywords?:number[]}) {
    return api(`/reviews/${id}`, { method:'PATCH', json: payload });
}
export async function reviewDelete(id:number){ return api(`/reviews/${id}`, { method:'DELETE' }); }
export async function reviewToggleLike(id:number){ return api(`/reviews/${id}/like`, { method:'POST' }); }
// 리뷰 카드 PNG(Blob) 업로드 → 공개 상대경로 URL("/uploads/..") 반환
export async function reviewCardUpload(blob: Blob): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append("image", blob, "card.png");
    return api("/reviews/card-image", { formData: fd });
}
export async function reviewsAll(params: { page?: number; size?: number; sort?: 'latest' | 'popular' } = {}) {
    const s = new URLSearchParams();
    if (params.page) s.set('page', String(params.page));
    if (params.size) s.set('size', String(params.size));
    if (params.sort) s.set('sort', params.sort);
    return api(`/reviews/all?${s.toString()}`);
}
export async function reviewsByBook(bookId:number, params:{sort?:'latest'|'popular'; page?:number; size?:number}={}) {
    const s = new URLSearchParams();
    if (params.sort) s.set('sort', params.sort);
    if (params.page) s.set('page', String(params.page));
    if (params.size) s.set('size', String(params.size));
    return api(`/reviews/book/${bookId}?${s.toString()}`);
}
export async function reviewBookStats(bookId:number) {
    return api(`/reviews/book/${bookId}/stats`);
}
export async function reviewMyForBook(bookId:number) {
    return api(`/reviews/my/book/${bookId}`);
}

// 달력 API
export async function calendarAdd(bookId: number | string, date: string) {
    return api('/calendar/add', {
        json: { book_id: Number(bookId), date }
    });
}
export async function calendarRemove(bookId: number, date: string) {
    return api('/calendar/remove', { json: { book_id: bookId, date } });
}
export async function calendarList(month: string /* 'YYYY-MM' */) {
    const s = new URLSearchParams(); s.set('month', month);
    return api(`/calendar?${s.toString()}`); // -> { month, items:[{date, covers:[...]}, ...] }
}

export async function calendarExists(bookId: number | string) {
    return api(`/calendar/exists?book_id=${Number(bookId)}`);
}

export async function calendarUpdate(bookId: number | string, toDate: string) {
    return api('/calendar/update', {
        json: { book_id: Number(bookId), to_date: toDate } // ← 필요한 것만
    });
}

export async function bookById(id:number){ return api(`/books/by-id/${id}`); }


// ---- Weekly Tox (Talk Tox!) ----

export type WeeklyToxAuthor = {
    id: number;
    name: string;
};

export type WeeklyTox = {
    id: number;
    title: string;
    content: string;        // plain text (네이티브 앱용)
    content_plain?: string | null;
    content_html?: string | null;  // HTML (웹 ContentPager용)
    image_url: string | null;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    author_id?: number | null;
    author?: WeeklyToxAuthor | null;
    likes_count?: number;
    views_count?: number;
    comments_count?: number;
    published_at?: string | null;
    reaction_like?: number;
    reaction_funny?: number;
    reaction_wow?: number;
    reaction_angry?: number;
    reaction_sad?: number;
    reactions_total?: number;
};

export async function weeklyToxList(params: {
    page?: number;
    size?: number;
} = {}) {
    const s = new URLSearchParams();
    if (params.page) s.set('page', String(params.page));
    if (params.size) s.set('size', String(params.size));

    const res = await api(`/tox?${s.toString()}`);
    // 백엔드 응답: { items, total, page, size }
    return {
        items: (res.items || []) as WeeklyTox[],
        total: res.total ?? 0,
        page: res.page ?? params.page ?? 1,
        size: res.size ?? params.size ?? 10,
    };
}

export async function weeklyToxGet(id: number): Promise<WeeklyTox> {
    const res = await api(`/tox/${id}`);
    // 백엔드 응답: { tox: {...} } 또는 바로 {...}
    return (res.tox ?? res) as WeeklyTox;
}

export type ToxReactionType = "LIKE" | "FUNNY" | "WOW" | "ANGRY" | "SAD";

export async function weeklyToxReact(
    id: number,
    type: ToxReactionType | null
): Promise<{
    ok: boolean;
    reactions: {
        like: number;
        funny: number;
        wow: number;
        angry: number;
        sad: number;
        total: number;
    };
    userReaction: ToxReactionType | null;
}> {
    return api(`/tox/${id}/react`, {
        method: "POST",
        json: { type },
    });
}
